import { Injectable } from '@angular/core';
import { from, Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppModeTypes } from '../helpers/app-mode.enum';
import { Lookup } from '../helpers/lookup';
import { ApiService } from './api.service';
import { BrowserService } from './browser.service';
import { DataStoreService } from './data-store.service';
import { DreamFindersMergeService } from './dream-finders-merge.service';

import {
  CommunityDataLevel,
  IAmenityMap,
  ICollection,
  ICommunity,
  IHotspotGroup,
  IIdInfo,
  ISetting
} from '@ml/common';

@Injectable({
  providedIn: 'root'
})
export class CommunityService {
  private communitySubject = new Subject<ICommunity>();
  private lastUpdated: Date;
  private updateInProgress = false;
  private secondaryCommunity: ICommunity;

  constructor(
    private lookup: Lookup,
    private store: DataStoreService,
    private api: ApiService,
    private browser: BrowserService,
    private dreamFindersMerge: DreamFindersMergeService
  ) {}

  get current$() {
    return this.communitySubject.asObservable();
  }

  get current() {
    return this.store.currentCommunity;
  }

  // Merged Beazer communities use the Dream Finders theme, so none of the Beazer specific UI applies.
  // The real ClientId stays Beazer so leads, favorites, etc. are unaffected.
  get isBeazer(): boolean {
    return (
      this.current?.ClientId === this.lookup.ClientIds.Beazer && !this.dreamFindersMerge.isActive
    );
  }

  set current(c: ICommunity) {
    this.store.currentCommunity = c;
    this.communitySubject.next(c);
  }

  getLastUpdate(): Date {
    return this.lastUpdated;
  }

  get(communityId: number, appMode?: AppModeTypes): Observable<ICommunity> {
    let storedCommunity: ICommunity;
    try {
      storedCommunity = this.current;
    } catch {
      // No community in store... Calling API next
    }

    if (storedCommunity && storedCommunity.CommunityId === communityId) return of(storedCommunity);

    this.lastUpdated = new Date();
    return this.getFromApi(communityId, appMode);
  }

  // no longer used as client preferred full window reload to ensure fresh files as well
  // leaving in case minds are changed... again :)
  updateCurrentInBackground() {
    try {
      const hasBeenAtLeast15Minutes =
        !this.lastUpdated || new Date().getTime() - this.lastUpdated.getTime() > 15 * 60 * 1000;

      if (this.current && hasBeenAtLeast15Minutes && !this.updateInProgress) {
        // no need to await since this it meant to happen silently in background
        this.getFromApi(this.current.CommunityId).toPromise();
        console.log('Updating community data in background...');
      }
    } catch (error) {
      console.warn('Failed to update community data in background', error);
    }
  }

  getOldPrintSettingsByCommunityId(communityId: number): Observable<ISetting[]> {
    return this.api.getByUrl(
      `settings/application/${null}/categoryname/Print/community/${communityId}`
    );
  }

  private getFromApi(communityId: number, appMode?: AppModeTypes) {
    this.updateInProgress = true;

    const dataLevel =
      appMode && appMode === AppModeTypes.HomeConfigurator
        ? CommunityDataLevel.HomeConfiguratorOnly
        : CommunityDataLevel.MyScpOnly;
    return this.api.getCommunityById(communityId, dataLevel).pipe(
      switchMap(community => from(this.dreamFindersMerge.apply(community, dataLevel))),
      map((community: ICommunity) => {
        this.updateInProgress = false;
        this.updateLotMappedFloorPlans(community);

        this.current = community;
        this.lastUpdated = new Date();
        return community;
      })
    );
  }

  // if a lot has a mapped InventoryHome, then its MappedFloorPlanIds should be updated to reflect that
  private updateLotMappedFloorPlans(community: ICommunity) {
    const inventoryHomesWithLot = community.Neighborhoods.flatMap(n =>
      n.FloorPlans.flatMap(fp => fp.InventoryHomes)
    ).filter(ih => ih.LotIds.length);

    for (const neighborhood of community.Neighborhoods) {
      for (const lot of neighborhood.Lots) {
        const ihForLot = inventoryHomesWithLot.filter(ih => ih.LotIds.includes(lot.LotId));
        if (ihForLot.length) {
          lot.MappedFloorPlanIds = ihForLot.map(
            ih => ({ ClientUniqueIdentifier: '', Id: ih.FloorPlanId }) as IIdInfo
          );
        }
      }
    }
  }

  getBaseApiUrl() {
    return `${this.lookup.ApiProductDataEndpoint}${this.store.currentCommunity.VirtualDirectory}`;
  }

  getBaseApiUrlForClient() {
    return `${this.lookup.ApiProductDataEndpoint}/${this.store.currentCommunity.ClientName}_${this.store.currentCommunity.ClientId}`;
  }

  getFormattedAddress(excludeStreet = false) {
    const c = this.store.currentCommunity;
    const parts = [];
    if (c.Address && !excludeStreet) parts.push(c.Address);
    if (c.City) parts.push(c.City);
    if (c.State) parts.push(c.State);

    let address = parts.join(', ');
    if (c.PostalCode) address += ' ' + c.PostalCode;

    return address;
  }

  getOverallMapHotspotGroup(): IHotspotGroup {
    const newVersion = this.store.currentCommunity.HotspotGroup.find(
      group => group.ParentType === 'myscp community map'
    );
    if (newVersion) return newVersion;

    return null;
  }

  hasOverallCommunityMap(): boolean {
    const hotspotGroup = this.getOverallMapHotspotGroup();
    return (
      hotspotGroup &&
      !!hotspotGroup.ImageFilename &&
      // when in compact mode we require hotspot markers
      (!this.browser.isCompact() || !!hotspotGroup.Hotspot.length)
    );
  }

  getAmenityMaps(): IAmenityMap[] {
    return this.store.currentCommunity.AmenityMaps;
  }

  getCollections(): ICollection[] {
    return this.store.currentCommunity.Collection ?? [];
  }

  getDisplayName() {
    const c = this.store.currentCommunity;
    return c.PublicName || c.Name;
  }

  wipeOutFloorPlanPricing() {
    const community = this.store.currentCommunity;
    for (const hood of community.Neighborhoods) {
      for (const fp of hood.FloorPlans) {
        fp.PriceRange = null;
        fp.Price = null;
      }
    }
  }

  async getSecondaryCommunity(communityName: string): Promise<ICommunity | null> {
    if (this.secondaryCommunity) return this.secondaryCommunity;
    const allProjectCommunities = await this.api.getCommunitiesByProjectId(
      this.store.currentCommunity.ProjectId
    );
    const secondaryCommunity = allProjectCommunities.find(
      c => c.Name === communityName || c.PublicName === communityName
    );
    if (secondaryCommunity) {
      this.secondaryCommunity = secondaryCommunity;
      return secondaryCommunity;
    }
    return null;
  }
}
